// src/features/tidsregistering/components/absence-planner/useDragCreate.js
import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Manages drag-to-create interactions on an AbsenceRow.
 *
 * The user presses the mouse button on the row background, drags across
 * day columns, then releases — producing a start/end date pair that is
 * forwarded to `onDragCreate`.
 *
 * Coordinate math:
 *   The row's left edge in screen-space == position of `firstVisible` day.
 *   offsetX = clientX - rect.left  →  day offset within the visible window.
 *   dayIndex = firstVisible + floor(offsetX / dayWidth).
 */
export function useDragCreate({ visibleDayIndices, dayWidth, getDateForDayIndex, onDragCreate }) {
  const [drag, setDrag] = useState(null); // { startIdx, endIdx } | null
  const rowRef = useRef(null);

  const firstVisible = visibleDayIndices[0] ?? 0;
  const lastVisible = visibleDayIndices[visibleDayIndices.length - 1] ?? firstVisible;

  const xToDayIndex = useCallback((clientX) => {
    if (!rowRef.current) return firstVisible;
    const rect = rowRef.current.getBoundingClientRect();
    const offsetX = clientX - rect.left;
    const raw = firstVisible + Math.floor(offsetX / dayWidth);
    return Math.max(firstVisible, Math.min(raw, lastVisible));
  }, [firstVisible, lastVisible, dayWidth]);

  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    // Don't start a drag on an existing bar — let AbsenceBar handle that click
    if (e.target.closest('[data-absence-bar="true"]')) return;
    e.preventDefault();
    const idx = xToDayIndex(e.clientX);
    setDrag({ startIdx: idx, endIdx: idx });
  }, [xToDayIndex]);

  const handleMouseMove = useCallback((e) => {
    setDrag((prev) => prev ? { ...prev, endIdx: xToDayIndex(e.clientX) } : null);
  }, [xToDayIndex]);

  const handleMouseUp = useCallback((e) => {
    setDrag((prev) => {
      if (!prev) return null;
      const endIdx = xToDayIndex(e.clientX);
      const startIndex = Math.min(prev.startIdx, endIdx);
      const endIndex = Math.max(prev.startIdx, endIdx);
      const startDate = getDateForDayIndex(startIndex);
      const endDate = getDateForDayIndex(endIndex);
      if (onDragCreate && startDate && endDate) {
        onDragCreate({ startDate, endDate });
      }
      return null;
    });
  }, [xToDayIndex, getDateForDayIndex, onDragCreate]);

  // Attach move/up to window so the drag continues if the mouse leaves the row
  useEffect(() => {
    if (!drag) return;
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [drag, handleMouseMove, handleMouseUp]);

  // Ghost bar pixel dimensions while dragging
  const ghostBar = drag
    ? {
        leftPx: (Math.min(drag.startIdx, drag.endIdx) - firstVisible) * dayWidth,
        widthPx: (Math.abs(drag.endIdx - drag.startIdx) + 1) * dayWidth - 2,
      }
    : null;

  return { rowRef, isDragging: !!drag, ghostBar, handleMouseDown };
}
